import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import { toast } from "react-toastify";

import { lang } from "../../../middleware/lang";
import { ConnectAccount } from "./models";
import {
  CREATE_CONNECT_LOGIN_LINK,
  CREATE_CONNECT_ONBOARDING_LINK,
  UNLINK_CONNECT_ACCOUNT,
  UPDATE_CONNECT_ACCOUNT,
  CreateConnectLoginLinkResponse,
  CreateConnectOnboardingLinkResponse,
  UnlinkConnectAccountResponse,
  UpdateConnectAccountResponse,
} from "./queries";

type Props = {
  account: ConnectAccount;
  onUnlinked?: () => void;
};

const statusLabels: Record<string, { text_es: string; text_en: string; color: string }> = {
  active: { text_es: "Activa", text_en: "Active", color: "#48BB78" },
  onboarding: { text_es: "En proceso", text_en: "Onboarding", color: "#FFBB28" },
  pending: { text_es: "Pendiente", text_en: "Pending", color: "#FFBB28" },
  restricted: { text_es: "Restringida", text_en: "Restricted", color: "#FF6028" },
  disabled: { text_es: "Desactivada", text_en: "Disabled", color: "#999" },
};

const AccountStatus: React.FC<Props> = ({ account, onUnlinked }) => {
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false);

  const [openDashboard, { loading: dashLoading }] =
    useMutation<CreateConnectLoginLinkResponse>(CREATE_CONNECT_LOGIN_LINK);
  const [resumeOnboarding, { loading: onbLoading }] =
    useMutation<CreateConnectOnboardingLinkResponse>(CREATE_CONNECT_ONBOARDING_LINK);
  const [unlinkAccount, { loading: unlinkLoading }] =
    useMutation<UnlinkConnectAccountResponse>(UNLINK_CONNECT_ACCOUNT);
  const [updateAccount, { loading: updateLoading }] =
    useMutation<UpdateConnectAccountResponse>(UPDATE_CONNECT_ACCOUNT);

  const statusInfo = statusLabels[account.status] || statusLabels.pending;

  const handleOpenDashboard = async () => {
    try {
      const { data } = await openDashboard();
      if (data?.createConnectLoginLink.url) {
        window.open(data.createConnectLoginLink.url, "_blank");
      }
    } catch (e: any) {
      toast.error(e.message || "Error");
    }
  };

  const handleResumeOnboarding = async () => {
    try {
      const { data } = await resumeOnboarding();
      if (data?.createConnectOnboardingLink.url) {
        window.location.href = data.createConnectOnboardingLink.url;
      }
    } catch (e: any) {
      toast.error(e.message || "Error");
    }
  };

  const handleUnlink = async () => {
    try {
      await unlinkAccount();
      toast.success(lang("Cuenta desvinculada exitosamente", "Account unlinked successfully"));
      setShowUnlinkConfirm(false);
      onUnlinked?.();
    } catch (e: any) {
      toast.error(e.message || "Error");
    }
  };

  const handleUpdate = async () => {
    try {
      const { data } = await updateAccount();
      if (data?.updateConnectAccount.url) {
        window.location.href = data.updateConnectAccount.url;
      }
    } catch (e: any) {
      toast.error(e.message || "Error");
    }
  };

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #ebebeb",
        borderRadius: 12,
        padding: "1.5rem",
        marginBottom: "1.5rem",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "1rem" }}>
        <div>
          <h5 style={{ fontWeight: 700, marginBottom: 4 }}>
            {lang("Cuenta conectada", "Connected account")}
          </h5>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                display: "inline-block",
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: statusInfo.color,
              }}
            />
            <span style={{ color: statusInfo.color, fontWeight: 600, fontSize: 14 }}>
              {lang(statusInfo.text_es, statusInfo.text_en)}
            </span>
            {account.email && (
              <span style={{ color: "#999", fontSize: 13 }}>
                &middot; {account.email}
              </span>
            )}
          </div>
          <span style={{ fontSize: 13, color: "#666", marginTop: 4, display: "block" }}>
            {lang("Comision de plataforma", "Platform commission")}: {Number(account.commission_rate).toFixed(1)}%
            {" · "}
            <a
              href="https://foo-blog.web.app/posts/comisiones-cobros-en-linea.html"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#FFBB28", fontWeight: 600, textDecoration: "none" }}
            >
              {lang("¿Cómo se calcula?", "How is it calculated?")}
            </a>
          </span>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {account.status === "active" && (
            <>
              <button
                className="btn btn-sm"
                style={{
                  background: "#292929",
                  color: "#fff",
                  borderRadius: 6,
                  fontWeight: 600,
                }}
                onClick={handleOpenDashboard}
                disabled={dashLoading}
              >
                {dashLoading ? (
                  <span className="spinner-border spinner-border-sm mr-1" />
                ) : (
                  <i className="fas fa-external-link-alt mr-1" />
                )}
                {lang("Panel de Stripe", "Stripe Dashboard")}
              </button>

              <button
                className="btn btn-sm"
                style={{
                  background: "#FFBB28",
                  color: "#292929",
                  borderRadius: 6,
                  fontWeight: 600,
                }}
                onClick={handleUpdate}
                disabled={updateLoading}
              >
                {updateLoading ? (
                  <span className="spinner-border spinner-border-sm mr-1" />
                ) : (
                  <i className="fas fa-pen mr-1" />
                )}
                {lang("Actualizar cuenta", "Update account")}
              </button>

              <button
                className="btn btn-sm"
                style={{
                  background: "#fff",
                  color: "#FF6028",
                  border: "1px solid #FF6028",
                  borderRadius: 6,
                  fontWeight: 600,
                }}
                onClick={() => setShowUnlinkConfirm(true)}
                disabled={unlinkLoading}
              >
                <i className="fas fa-unlink mr-1" />
                {lang("Desvincular", "Unlink")}
              </button>
            </>
          )}

          {(account.status === "onboarding" || account.status === "restricted") && (
            <>
              <button
                className="btn btn-sm"
                style={{
                  background: "#FFBB28",
                  color: "#292929",
                  borderRadius: 6,
                  fontWeight: 600,
                }}
                onClick={handleResumeOnboarding}
                disabled={onbLoading}
              >
                {onbLoading ? (
                  <span className="spinner-border spinner-border-sm mr-1" />
                ) : (
                  <i className="fas fa-arrow-right mr-1" />
                )}
                {lang("Completar verificacion", "Complete verification")}
              </button>

              <button
                className="btn btn-sm"
                style={{
                  background: "#fff",
                  color: "#FF6028",
                  border: "1px solid #FF6028",
                  borderRadius: 6,
                  fontWeight: 600,
                }}
                onClick={() => setShowUnlinkConfirm(true)}
                disabled={unlinkLoading}
              >
                <i className="fas fa-unlink mr-1" />
                {lang("Desvincular", "Unlink")}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Unlink confirmation modal */}
      {showUnlinkConfirm && (
        <div
          style={{
            marginTop: "1rem",
            padding: "1rem",
            background: "#FFF5F5",
            border: "1px solid #FF6028",
            borderRadius: 8,
          }}
        >
          <p style={{ margin: 0, fontWeight: 600, color: "#292929", fontSize: 14 }}>
            {lang(
              "¿Estas seguro de desvincular tu cuenta de Stripe?",
              "Are you sure you want to unlink your Stripe account?"
            )}
          </p>
          <p style={{ margin: "8px 0", color: "#666", fontSize: 13 }}>
            {lang(
              "Ya no podras recibir pagos en linea hasta que vincules una nueva cuenta.",
              "You will no longer be able to receive online payments until you link a new account."
            )}
          </p>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="btn btn-sm"
              style={{
                background: "#FF6028",
                color: "#fff",
                borderRadius: 6,
                fontWeight: 600,
              }}
              onClick={handleUnlink}
              disabled={unlinkLoading}
            >
              {unlinkLoading ? (
                <span className="spinner-border spinner-border-sm mr-1" />
              ) : (
                <i className="fas fa-trash mr-1" />
              )}
              {lang("Si, desvincular", "Yes, unlink")}
            </button>
            <button
              className="btn btn-sm"
              style={{
                background: "#fff",
                color: "#292929",
                border: "1px solid #ebebeb",
                borderRadius: 6,
                fontWeight: 600,
              }}
              onClick={() => setShowUnlinkConfirm(false)}
            >
              {lang("Cancelar", "Cancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountStatus;
